import catholicLimits from '../assets/bible/book_limits_catholic.json';
import protestantLimits from '../assets/bible/book_limits_protestant.json';
import frBookMap from '../assets/bible/fr/book_map.json';
import enBookMap from '../assets/bible/en/book_map.json';
import itBookMap from '../assets/bible/it/book_map.json';
import esBookMap from '../assets/bible/es/book_map.json';
import frDisplayNames from '../assets/bible/fr/display_names.json';
import enDisplayNames from '../assets/bible/en/display_names.json';
import itDisplayNames from '../assets/bible/it/display_names.json';
import esDisplayNames from '../assets/bible/es/display_names.json';

// Type definitions
export type BibleTradition = 'catholic' | 'protestant';
type BookInfo = {
  book: string;
  max_chapters: number;
  max_verses_per_chapter: number[];
};

// Book map type and mapping by locale
type BookMap = { [key: string]: string };
const BOOK_MAPS: { [locale: string]: BookMap } = {
  'fr': frBookMap,
  'en': enBookMap,
  'it': itBookMap,
  'es': esBookMap,
};

// Display names mapping by locale
const DISPLAY_NAMES: { [locale: string]: Record<string, string> } = {
  'fr': frDisplayNames,
  'en': enDisplayNames,
  'it': itDisplayNames,
  'es': esDisplayNames,
};

// Map translations to their traditions
const TRANSLATION_TRADITIONS: Record<string, BibleTradition> = {
  // Catholic translations
  'aelf': 'catholic',
  'jerusalembible': 'catholic',
  'drb': 'catholic',
  'bdp': 'catholic',
  'cei08': 'catholic',
  // Protestant translations
  'kjv': 'protestant',
  'esv': 'protestant',
  'niv': 'protestant',
  'lsg': 'protestant',
  'mar': 'protestant',
  'dar': 'protestant',
  'lnd': 'protestant',
  'nr20': 'protestant',
  'bl95': 'protestant',
  'rv60': 'protestant',
  'nvi22': 'protestant',
  'eu': 'catholic',
  'elb': 'protestant',
  'lut': 'protestant',
  'sch2000': 'protestant',
  
  // Default to protestant for unknown translations
  'default': 'protestant'
};

// Map translations to their languages
const TRANSLATION_LANGUAGES: Record<string, string> = {
  // French translations
  'aelf': 'fr',
  'lsg': 'fr',
  'mar': 'fr',
  'dar': 'fr',
  'bdp': 'fr',
  
  // English translations
  'drb': 'en',
  'kjv': 'en',
  'esv': 'en',
  'niv': 'en',
  
  // Italian translations
  'cei08': 'it',
  'lnd': 'it',
  'nr20': 'it',
  'tilc': 'it',
  
  // Spanish translations
  'bl95': 'es',
  'rv60': 'es',
  'nvi22': 'es',
  
  // German translations
  'eu': 'de',
  'elb': 'de',
  'lut': 'de',
  'sch2000': 'de',
  
  'default': 'fr'
};

// Helper function to get language for a translation
export function getLanguageForTranslation(translation: string): string {
  const frenchTranslations = ['aelf', 'lsg', 'mar', 'dar', 'bdp'];
  const englishTranslations = ['kjv', 'drb', 'esv', 'niv', 'nabre'];
  const italianTranslations = ['cei08', 'lnd', 'nr20', 'tilc'];
  const spanishTranslations = ['bl95', 'rv60', 'nvi22'];
  const germanTranslations = ['eu', 'elb', 'lut', 'sch2000'];
  
  translation = translation.toLowerCase();
  
  if (frenchTranslations.includes(translation)) {
    return 'fr';
  }
  
  if (spanishTranslations.includes(translation)) {
    return 'es';
  }
  
  if (germanTranslations.includes(translation)) {
    return 'de';
  }
  
  if (italianTranslations.includes(translation)) {
    return 'it';
  }
  
  if (spanishTranslations.includes(translation)) {
    return 'es';
  }
  
  // Default to English if unknown
  return 'en';
}

// Helper function to normalize book names using the appropriate book map
export function normalizeBookName(bookName: string, locale: string): string {
  if (!bookName) return '';
  
  // Prepare the input: lowercase, trim whitespace
  let normalizedInput = bookName.toLowerCase().trim();
  
  // Get the appropriate book map based on locale
  const bookMap = BOOK_MAPS[locale] || BOOK_MAPS['fr'];
  
  // Check if the normalized input exists in the book map
  if (bookMap[normalizedInput]) {
    return bookMap[normalizedInput];
  }
  
  // If not found directly, try removing spaces
  const noSpaces = normalizedInput.replace(/\s+/g, '');
  if (bookMap[noSpaces]) {
    return bookMap[noSpaces];
  }
  
  // If still not found, return the original book name
  // This will likely fail validation later, which is expected
  return bookName;
}

// Helper function to get tradition for a translation
export function getTraditionForTranslation(translation: string): BibleTradition {
  const lowercaseTranslation = translation.toLowerCase();
  return TRANSLATION_TRADITIONS[lowercaseTranslation] || TRANSLATION_TRADITIONS.default;
}

// Get all book codes for a given tradition
export function getAllBookCodes(tradition: BibleTradition): string[] {
  const limits = tradition === 'catholic' ? catholicLimits : protestantLimits;
  const oldTestament = limits["Ancien Testament"].map(book => book.book);
  const newTestament = limits["Nouveau Testament"].map(book => book.book);
  return [...oldTestament, ...newTestament];
}

// Find book info by code, using proper normalization
function findBookInfo(bookName: string, tradition: BibleTradition, locale: string): BookInfo | null {
  // Remove forced Protestant logic for Psalms
  const normalizedBookCode = normalizeBookName(bookName, locale);
  // Use the tradition as provided
  const limits = tradition === 'catholic' ? catholicLimits : protestantLimits;
  
  // Debug logging
  console.log(`Looking for book info: "${bookName}" → "${normalizedBookCode}" (locale: ${locale}, tradition: ${tradition})`);
  
  // Check in both Old and New Testament
  for (const testament of ["Ancien Testament", "Nouveau Testament"] as const) {
    const book = limits[testament]?.find(b => 
      b.book.toLowerCase() === normalizedBookCode.toLowerCase()
    );
    if (book) {
      // Fix the syntax error in the template string - was using a comma incorrectly
      console.log(`Found book: ${book.book}, max chapters: ${book.max_chapters}`);
      return book;
    }
  }
  
  console.log(`Book not found: ${bookName} (normalized: ${normalizedBookCode})`);
  return null;
}

// Validate if a book exists in the given tradition
export function isValidBook(
  bookName: string, 
  translationOrTradition: string | BibleTradition
): boolean {
  let tradition: BibleTradition;
  let locale = 'fr';
  
  if (translationOrTradition === 'catholic' || translationOrTradition === 'protestant') {
    tradition = translationOrTradition;
  } else {
    tradition = getTraditionForTranslation(translationOrTradition);
    locale = getLanguageForTranslation(translationOrTradition);
  }
  
  return findBookInfo(bookName, tradition, locale) !== null;
}

// Get maximum chapter number for a book
export function getMaxChapter(
  bookName: string, 
  translationOrTradition: string | BibleTradition
): number | null {
  let tradition: BibleTradition;
  let locale = 'fr';
  
  if (translationOrTradition === 'catholic' || translationOrTradition === 'protestant') {
    tradition = translationOrTradition;
  } else {
    tradition = getTraditionForTranslation(translationOrTradition);
    locale = getLanguageForTranslation(translationOrTradition);
  }
  
  const book = findBookInfo(bookName, tradition, locale);
  return book ? book.max_chapters : null;
}

// Validate if a chapter exists in a book
export function isValidChapter(
  bookName: string, 
  chapter: number,
  translationOrTradition: string | BibleTradition
): boolean {
  const maxChapter = getMaxChapter(bookName, translationOrTradition);
  return maxChapter !== null && chapter > 0 && chapter <= maxChapter;
}

// Get maximum verse number for a chapter
export function getMaxVerse(
  bookName: string, 
  chapter: number,
  translationOrTradition: string | BibleTradition
): number | null {
  let tradition: BibleTradition;
  let locale = 'fr';
  
  if (translationOrTradition === 'catholic' || translationOrTradition === 'protestant') {
    tradition = translationOrTradition;
  } else {
    tradition = getTraditionForTranslation(translationOrTradition);
    locale = getLanguageForTranslation(translationOrTradition);
  }
  const book = findBookInfo(bookName, tradition, locale);
  if (!book || chapter <= 0 || chapter > book.max_chapters) {
    return null;
  }
  return book.max_verses_per_chapter[chapter - 1];
}

// Validate if a verse exists in a chapter
export function isValidVerse(
  bookName: string, 
  chapter: number,
  verse: number,
  translationOrTradition: string | BibleTradition
): boolean {
  let locale = 'fr';
  if (translationOrTradition !== 'catholic' && translationOrTradition !== 'protestant') {
    locale = getLanguageForTranslation(translationOrTradition);
  }
  const maxVerse = getMaxVerse(bookName, chapter, translationOrTradition);
  return maxVerse !== null && verse > 0 && verse <= maxVerse;
}

// Validate a complete reference (book, chapter, and optional verse range)
export function validateReference(
  bookName: string,
  chapter: number,
  startVerse?: number,
  endVerse?: number,
  translationOrTradition: string | BibleTradition = 'protestant',
  language: string = 'en' // Language parameter for both UI and book normalization
): { valid: boolean; error?: string } {
  console.log(`validateReference called with: bookName="${bookName}", chapter=${chapter}, ` +
               `startVerse=${startVerse}, endVerse=${endVerse}, translation=${translationOrTradition}, ` +
               `language=${language}`);
  
  // Simplify: use the language parameter for both UI and book normalization
  const locale = language === 'fr' ? 'fr' : 'en';
  let tradition: BibleTradition;
  
  if (translationOrTradition === 'catholic' || translationOrTradition === 'protestant') {
    tradition = translationOrTradition;
  } else {
    tradition = getTraditionForTranslation(translationOrTradition);
  }
  
  console.log(`Using tradition: ${tradition}, locale: ${locale}`);
  
  // Always use Protestant for Psalms
  const normalizedBookCode = normalizeBookName(bookName, locale);
  console.log(`Normalized book code: "${bookName}" -> "${normalizedBookCode}" (using locale: ${locale})`);
  
  // Get display name in the UI language
  const displayName = getBookDisplayName(normalizedBookCode, locale);
  console.log(`Display name for errors: "${displayName}" (using locale: ${locale})`);
  
  // Check if book exists
  const bookExists = isValidBook(normalizedBookCode, tradition);
  console.log(`Book validation: exists=${bookExists}, using tradition=${tradition}`);
  
  if (!bookExists) {
    const errorMsg = locale === 'fr'
      ? `Le livre "${displayName}" n'existe pas dans cette version de la Bible`
      : `Book "${displayName}" does not exist in this Bible version`;
    console.log(`Book validation failed: ${errorMsg}`);
    return {
      valid: false,
      error: errorMsg
    };
  }

  // Check if chapter exists
  const chapterExists = isValidChapter(normalizedBookCode, chapter, tradition);
  const maxChapter = getMaxChapter(normalizedBookCode, tradition);
  console.log(`Chapter validation: exists=${chapterExists}, chapter=${chapter}, maxChapter=${maxChapter}`);
  
  if (!chapterExists) {
    const errorMsg = locale === 'fr'
      ? `Le chapitre ${chapter} n'existe pas dans ${displayName}. Plage valide : 1-${maxChapter || '?'}`
      : `Chapter ${chapter} does not exist in ${displayName}. Valid range: 1-${maxChapter || '?'}`;
    console.log(`Chapter validation failed: ${errorMsg}`);
    return {
      valid: false,
      error: errorMsg
    };
  }

  // If verses are provided, check their validity
  if (startVerse !== undefined) {
    const startVerseExists = isValidVerse(normalizedBookCode, chapter, startVerse, tradition);
    const maxVerse = getMaxVerse(normalizedBookCode, chapter, tradition);
    console.log(`Start verse validation: exists=${startVerseExists}, verse=${startVerse}, maxVerse=${maxVerse}`);
    
    if (!startVerseExists) {
      const errorMsg = locale === 'fr'
        ? `Le verset ${startVerse} n'existe pas dans ${displayName} ${chapter}. Plage valide : 1-${maxVerse || '?'}`
        : `Verse ${startVerse} does not exist in ${displayName} ${chapter}. Valid range: 1-${maxVerse || '?'}`;
      console.log(`Start verse validation failed: ${errorMsg}`);
      return {
        valid: false,
        error: errorMsg
      };
    }

    // If end verse is provided, validate it as well
    if (endVerse !== undefined) {
      console.log(`Validating end verse: ${endVerse} (comparing to start verse: ${startVerse})`);
      
      if (endVerse < startVerse) {
        const errorMsg = locale === 'fr'
          ? `Le verset final (${endVerse}) ne peut pas être inférieur au verset initial (${startVerse})`
          : `End verse (${endVerse}) cannot be less than start verse (${startVerse})`;
        console.log(`End verse validation failed: ${errorMsg}`);
        return {
          valid: false,
          error: errorMsg
        };
      }

      const endVerseExists = isValidVerse(normalizedBookCode, chapter, endVerse, tradition);
      console.log(`End verse validation: exists=${endVerseExists}, verse=${endVerse}, maxVerse=${maxVerse}`);
      
      if (!endVerseExists) {
        const errorMsg = locale === 'fr'
          ? `Le verset ${endVerse} n'existe pas dans ${displayName} ${chapter}. Plage valide : 1-${maxVerse || '?'}`
          : `Verse ${endVerse} does not exist in ${displayName} ${chapter}. Valid range: 1-${maxVerse || '?'}`;
        console.log(`End verse validation failed: ${errorMsg}`);
        return {
          valid: false,
          error: errorMsg
        };
      }
    }
  }

  console.log(`Reference validation succeeded: ${bookName} ${chapter}${startVerse ? `:${startVerse}${endVerse && endVerse !== startVerse ? `-${endVerse}` : ''}` : ''}`);
  return { valid: true };
}

// Get display name for a book code in the specified locale
export function getBookDisplayName(bookCode: string, locale: string = 'fr'): string {
  if (!bookCode) return '';
  
  // Get the display names for the locale
  const displayNames = DISPLAY_NAMES[locale] || DISPLAY_NAMES['fr'];
  
  // Book codes in our JSON are capitalized, ensure consistent format
  const standardBookCode = bookCode.charAt(0).toUpperCase() + bookCode.slice(1).toLowerCase();
  
  // Return the exact display name from the JSON or the original code if not found
  return displayNames[standardBookCode] || bookCode;
}

// Format a reference with proper display names
export function formatReference(
  bookCode: string,
  chapter: number | string,
  startVerse?: number | string,
  endVerse?: number | string,
  locale: string = 'fr'
): string {
  // Book codes should be normalized to standard format first
  const standardBookCode = bookCode.charAt(0).toUpperCase() + bookCode.slice(1).toLowerCase();
  
  // Get the exact display name with diacritics from JSON
  const displayName = getBookDisplayName(standardBookCode, locale);
  
  // Format the reference with the unmodified display name
  let reference = `${displayName} ${chapter}`;
  
  if (startVerse) {
    if (endVerse && startVerse !== endVerse) {
      reference += `,${startVerse}-${endVerse}`;
    } else {
      reference += `,${startVerse}`;
    }
  }
  
  return reference;
}

// Export book limits data for direct access
export const bookLimits = {
  catholic: catholicLimits,
  protestant: protestantLimits
};
